import { useCallback, useEffect, useRef, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { View } from 'foundations-components/components/View';
import { CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import AIButton from 'ai-components-ui-library/components/buttons/AIButton';
import { fetchLatestPreview } from '../../../api/ChatSpotV1HighVolumeApi';
import BreezeTile from './breeze/BreezeTile';
import CopilotPreviewInner from './CopilotPreviewInner';
import { useStreamingAction } from '../../../hooks/useStreamingAction';
import { toEventResponse } from './utils/LatestPreviewApi';
import { getIsPastRefreshThreshold } from './utils/RefreshButton';
import { useIsDisabledByAccessControls } from './hooks/useIsDisabledByAccessControls';
import { DisabledPreviewControls } from './DisabledPreviewControls';
import CopilotPreviewInnerExpandable from './CopilotPreviewInnerExpandable';
import { useShouldShowActions } from '../../../hooks/useShouldShowActions';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CopilotPreviewAutoGenerated = ({
  actionPayload,
  aiTracker,
  copilotAction,
  children,
  hasAIBadge,
  previewKey,
  previewSource,
  previewTitle,
  numHoursTilSummaryStaleRefresh,
  onChatWithCopilot,
  onPositiveFeedback,
  onNegativeFeedback,
  onRefresh,
  onPreviewShown,
  onQuickActionClick,
  usageTrackerAppLocationKey,
  overrideDisabled,
  onDontShowAgain,
  isExpandable,
  defaultExpanded,
  maxHeight
}) => {
  const previewKeyRef = useRef('');
  const [latestPreviewLoading, setLatestPreviewLoading] = useState(true);
  const [latestPreview, setLatestPreview] = useState();
  const previousDisabledRef = useRef(Boolean(overrideDisabled === null || overrideDisabled === void 0 ? void 0 : overrideDisabled.isDisabled));
  const {
    isDisabledByAccessControls,
    isAccessRequestLoading
  } = useIsDisabledByAccessControls();
  const {
    sendAction,
    actionResponse,
    isStreaming,
    onReset
  } = useStreamingAction();
  const shouldShowActions = useShouldShowActions();
  const handleReset = useCallback(() => {
    setLatestPreview(undefined);
    onReset();
  }, [onReset]);
  const hasPreviewKeyChanged = previewKey && previewKeyRef.current !== previewKey;
  const isDisabled = (overrideDisabled === null || overrideDisabled === void 0 ? void 0 : overrideDisabled.isDisabled) || isDisabledByAccessControls;
  const hasJustBeenEnabled = previousDisabledRef.current && !isDisabled;
  previousDisabledRef.current = Boolean(overrideDisabled === null || overrideDisabled === void 0 ? void 0 : overrideDisabled.isDisabled);
  useEffect(() => {
    if ((hasPreviewKeyChanged || hasJustBeenEnabled) && !isAccessRequestLoading) {
      previewKeyRef.current = previewKey;
      aiTracker.track('Ai Addon Copilot Preview', 'Copilot Preview Interaction', {
        action: 'primary_action_auto_generated',
        command: JSON.stringify(copilotAction)
      });
      handleReset();
      setLatestPreviewLoading(true);
      fetchLatestPreview({
        previewSource,
        previewKey,
        isAutoGenerated: true
      }).then(response => {
        if (response.isStale && !isDisabled) {
          sendAction(actionPayload);
        } else {
          const latestPreviewData = toEventResponse(response);

          // this is to refresh the preview if it has been 1 hour since it was last generated
          const isStaleForRefresh = getIsPastRefreshThreshold({
            date: latestPreviewData.timestamp,
            threshold: numHoursTilSummaryStaleRefresh
          });
          if (isStaleForRefresh && !isDisabled) {
            sendAction(actionPayload);
          } else {
            setLatestPreview(latestPreviewData);
          }
        }
        setLatestPreviewLoading(false);
      }).catch(err => {
        setLatestPreviewLoading(false);
        if (err.status === 404 && !isDisabled) {
          sendAction(actionPayload);
        }
      });
    }
  }, [aiTracker, copilotAction, previewKey, previewSource, actionPayload, sendAction, hasPreviewKeyChanged, handleReset, onReset, numHoursTilSummaryStaleRefresh, isDisabled, isAccessRequestLoading, hasJustBeenEnabled]);
  const handleExpand = useCallback(isExpanding => {
    aiTracker.track('Ai Addon Copilot Preview', 'Copilot Preview Interaction', {
      action: isExpanding ? 'expand' : 'collapse'
    });
  }, [aiTracker]);
  const handleRefresh = useCallback(() => {
    if (onRefresh) {
      onRefresh();
    }
    setLatestPreview(undefined);
    onReset();
    aiTracker.track('Ai Addon Copilot Preview', 'Copilot Preview Interaction', {
      action: 'refresh_summary'
    });
    const payload = {
      data: Object.assign({}, actionPayload.data, {
        chatPreview: Object.assign({}, actionPayload.data.chatPreview, {
          isAutoGenerated: false
        })
      }),
      isRefresh: true
    };
    sendAction(payload);
  }, [actionPayload, onRefresh, onReset, aiTracker, sendAction]);
  useEffect(() => {
    if (onPreviewShown && latestPreview) {
      onPreviewShown(latestPreview);
    }
  }, [latestPreview, onPreviewShown]);
  const activeResponse = latestPreview || actionResponse;
  const isLoading = latestPreviewLoading || isAccessRequestLoading;
  const renderActions = () => {
    if (isDisabled) {
      return /*#__PURE__*/_jsx(DisabledPreviewControls, {
        usageTracker: aiTracker,
        onDontShowAgain: onDontShowAgain,
        isDisabledDescriptionOverriden: Boolean(overrideDisabled === null || overrideDisabled === void 0 ? void 0 : overrideDisabled.disabledDescription)
      });
    }
    if (!shouldShowActions) {
      return null;
    }
    return /*#__PURE__*/_jsx(View, {
      marginBlockStart: isExpandable && !isStreaming ? '0' : '400',
      as: "div",
      children: !isLoading && /*#__PURE__*/_jsx(AIButton, {
        onClick: () => {
          if (onChatWithCopilot) {
            onChatWithCopilot(activeResponse);
          }
        },
        disabled: isStreaming || isLoading,
        tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "aiAddonCopilotPreview.components.copilotPreview.chatWithCopilotButton.tooltip"
        }),
        size: "xs",
        use: "secondary",
        usageTracker: aiTracker,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "aiAddonCopilotPreview.components.copilotPreview.chatWithCopilotButton.message"
        })
      })
    });
  };
  if (isExpandable) {
    return /*#__PURE__*/_jsx(CardWrapper, {
      compact: true,
      children: /*#__PURE__*/_jsxs(CardSection, {
        children: [/*#__PURE__*/_jsx(CopilotPreviewInnerExpandable, {
          hasAIBadge: hasAIBadge,
          previewTitle: previewTitle,
          onRefresh: handleRefresh,
          onPositiveFeedback: onPositiveFeedback,
          onNegativeFeedback: onNegativeFeedback,
          onQuickActionClick: onQuickActionClick,
          response: activeResponse,
          usageTracker: aiTracker,
          usageTrackerAppLocationKey: usageTrackerAppLocationKey,
          overrideDisabled: overrideDisabled,
          maxHeight: maxHeight,
          defaultExpanded: defaultExpanded,
          onExpand: handleExpand,
          isLoading: isLoading,
          isStreaming: isStreaming,
          children: children
        }), renderActions()]
      })
    });
  }
  return /*#__PURE__*/_jsxs(BreezeTile, {
    children: [/*#__PURE__*/_jsx(CopilotPreviewInner, {
      hasAIBadge: hasAIBadge,
      previewTitle: previewTitle,
      onRefresh: handleRefresh,
      onPositiveFeedback: onPositiveFeedback,
      onNegativeFeedback: onNegativeFeedback,
      onQuickActionClick: onQuickActionClick,
      response: activeResponse,
      usageTracker: aiTracker,
      usageTrackerAppLocationKey: usageTrackerAppLocationKey,
      overrideDisabled: overrideDisabled,
      isLoading: isLoading,
      isStreaming: isStreaming,
      children: children
    }), renderActions()]
  });
};
export default CopilotPreviewAutoGenerated;